// Generic share-image button. Builds an /api/og/<endpoint> URL with
// the supplied query params and either:
//   · invokes the OS share sheet (mobile, where navigator.share +
//     navigator.canShare(files) are available), OR
//   · downloads the PNG file (desktop / browsers without share API).
//
// Reuses the existing @vercel/og pipeline (api/og/quiz-result.tsx
// pattern) — share images are pre-rendered server-side, this
// component just fetches one and hands it to the user.
//
// Behaviour notes:
//   · Loading state while the OG endpoint generates (Satori
//     rasterising at the edge typically <1.5s after font cache warm).
//   · On error: toast + remains usable (no destructive state).
//   · Filename: `lovin-malta-<asset>-<date>.png` so the user can
//     find it again in their downloads.

import { useState } from 'react';

interface Props {
  /** OG endpoint name — maps to `/api/og/<endpoint>`.
   *  e.g. 'turnout' → /api/og/turnout */
  endpoint: 'turnout' | 'polls-open';
  /** Query params encoded into the URL. */
  params: Record<string, string>;
  /** Short asset name baked into the download filename. */
  assetName: string;
  /** Button label. Defaults to "Share". */
  label?: string;
  /** Optional aria-label override. */
  ariaLabel?: string;
  /** Visual variant. `pill` = compact pill (default). `chip` = even
   *  smaller, used inside dense layouts. */
  variant?: 'pill' | 'chip';
  /** Inline style passthrough — primarily for caller-supplied
   *  positioning (e.g. align self) without forking the variant. */
  style?: React.CSSProperties;
}

export default function ShareButton({
  endpoint,
  params,
  assetName,
  label = 'Share',
  ariaLabel,
  variant = 'pill',
  style,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ ...params, size: 'ig' }).toString();
      const url = `/api/og/${endpoint}?${qs}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = `lovin-malta-${assetName}-${todayStamp()}.png`;
      const file = new File([blob], filename, { type: blob.type });

      // Prefer the OS share sheet on devices that support files —
      // mobile flow is one tap into Instagram / WhatsApp / etc.
      const nav: NavigatorWithShare = navigator as NavigatorWithShare;
      if (
        typeof nav.share === 'function' &&
        typeof nav.canShare === 'function' &&
        nav.canShare({ files: [file] })
      ) {
        try {
          await nav.share({
            files: [file],
            title: 'Lovin Malta Election 2026',
            text: 'Live from Lovin Malta · Election 2026',
          });
          return;
        } catch (shareErr) {
          // User cancelled the share sheet — silent, not an error.
          if (
            shareErr instanceof DOMException &&
            (shareErr.name === 'AbortError' || shareErr.name === 'NotAllowedError')
          ) {
            return;
          }
          // Anything else falls through to download.
        }
      }

      // Desktop fallback: trigger a download.
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Safari has time to attach the URL.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      console.warn('[ShareButton]', msg);
    } finally {
      setBusy(false);
    }
  }

  const baseStyle: React.CSSProperties =
    variant === 'pill'
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          background: 'var(--red, #ff1349)',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          fontFamily: "var(--font-display, 'Poppins', sans-serif)",
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
        }
      : {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 10px',
          background: 'transparent',
          color: 'var(--red, #ff1349)',
          border: '1px solid var(--red, #ff1349)',
          borderRadius: 999,
          fontFamily: "var(--font-display, 'Poppins', sans-serif)",
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
        };

  const titleAttr =
    error !== null
      ? `Share failed: ${error}. Click to retry.`
      : 'Save / share as a 1080×1350 PNG (Instagram-ready)';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={ariaLabel ?? `${label} — saves a 1080×1350 PNG`}
      style={{ ...baseStyle, ...style }}
      title={titleAttr}
    >
      <span aria-hidden="true" style={{ fontSize: 13 }}>
        {busy ? '…' : '↗'}
      </span>
      <span>{busy ? 'Generating…' : label}</span>
    </button>
  );
}

// ── helpers ──────────────────────────────────────────────────────

interface NavigatorWithShare {
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
}

function todayStamp(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
